import type { Commitment } from '#commitments/commitment';
import type { Income } from '#income/income';

export type ProjectionScenario = 'conservative' | 'expected';

export type ProjectionPolicyExclusionReason =
  | 'already-reflected-in-current-cash'
  | 'cancelled'
  | 'scenario-excluded';

export type ProjectionPolicyDecision = Readonly<
  | { included: true }
  | {
      included: false;
      reason: ProjectionPolicyExclusionReason;
    }
>;

export type ProjectionPolicy = Readonly<{
  scenario: ProjectionScenario;
  evaluateIncome: (income: Income) => ProjectionPolicyDecision;
  evaluateCommitment: (commitment: Commitment) => ProjectionPolicyDecision;
}>;

const INCLUDED = Object.freeze({
  included: true,
}) satisfies ProjectionPolicyDecision;
const ALREADY_REALIZED = Object.freeze({
  included: false,
  reason: 'already-reflected-in-current-cash',
}) satisfies ProjectionPolicyDecision;
const CANCELLED = Object.freeze({
  included: false,
  reason: 'cancelled',
}) satisfies ProjectionPolicyDecision;
const SCENARIO_EXCLUDED = Object.freeze({
  included: false,
  reason: 'scenario-excluded',
}) satisfies ProjectionPolicyDecision;

export const conservativeProjectionPolicy: ProjectionPolicy = Object.freeze({
  scenario: 'conservative',
  evaluateIncome(income: Income): ProjectionPolicyDecision {
    if (income.status === 'received') {
      return ALREADY_REALIZED;
    }
    if (income.status === 'cancelled') {
      return CANCELLED;
    }
    return income.kind === 'fixed' ? INCLUDED : SCENARIO_EXCLUDED;
  },
  evaluateCommitment(commitment: Commitment): ProjectionPolicyDecision {
    return evaluateCommitmentLifecycle(commitment);
  },
});

export const expectedProjectionPolicy: ProjectionPolicy = Object.freeze({
  scenario: 'expected',
  evaluateIncome(income: Income): ProjectionPolicyDecision {
    if (income.status === 'received') {
      return ALREADY_REALIZED;
    }
    if (income.status === 'cancelled') {
      return CANCELLED;
    }
    return INCLUDED;
  },
  evaluateCommitment(commitment: Commitment): ProjectionPolicyDecision {
    return evaluateCommitmentLifecycle(commitment);
  },
});

function evaluateCommitmentLifecycle(
  commitment: Commitment,
): ProjectionPolicyDecision {
  if (commitment.status === 'paid') {
    return ALREADY_REALIZED;
  }
  if (commitment.status === 'cancelled') {
    return CANCELLED;
  }
  return INCLUDED;
}
